import { createRoute, redirect, type AnyRoute } from "@tanstack/react-router";
import { TrendingUp } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import { CsmsPage } from "./pages/CsmsPage";
import { DataPage } from "./pages/DataPage";
import { PipelinePage } from "./pages/PipelinePage";
import { RenewalsDashboardPage } from "./pages/DashboardPage";

/** Search params on /renewals/pipeline. The drawer reads `account`+`expanded`;
 *  the page reads filter/sort state. All filters live in the URL so back/
 *  forward and shareable links preserve the working view. */
export interface PipelineSearch {
  // drawer state
  account?: number;
  expanded?: boolean;
  // table state
  anchor?: "billing" | "contract";
  view?: "upcoming" | "past";
  showAll?: boolean;
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  // multi-select filters (comma-separated in the URL string form)
  status?: string[];
  forecast?: string[];
  csm?: string[];
  month?: string[];
  industry?: string[];
  tier?: string[];
  ctype?: string[];
  /** "Setup" mode: show only accounts that still need first_term_start_date.
   *  The temporary SubNav "Setup" tab is just a deep link with this flag on. */
  setup?: boolean;
}

/** Search params on /renewals/data. The active data-quality tab lives in the
 *  URL so the back button returns to the tab you were last on. */
export interface RenewalsDataSearch {
  tab?: "contracts" | "accounts" | "pos-no-master" | "pos-no-company";
}

function validateRenewalsDataSearch(
  raw: Record<string, unknown>,
): RenewalsDataSearch {
  const out: RenewalsDataSearch = {};
  if (
    raw.tab === "contracts" ||
    raw.tab === "accounts" ||
    raw.tab === "pos-no-master" ||
    raw.tab === "pos-no-company"
  ) {
    out.tab = raw.tab;
  }
  return out;
}

function parseList(raw: unknown): string[] | undefined {
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v)).filter(Boolean);
  }
  if (typeof raw === "string" && raw.length > 0) {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return undefined;
}

function validatePipelineSearch(
  raw: Record<string, unknown>,
): PipelineSearch {
  const out: PipelineSearch = {};
  if (raw.account !== undefined && raw.account !== "") {
    const n = Number(raw.account);
    if (Number.isFinite(n)) out.account = n;
  }
  if (raw.expanded === true || raw.expanded === "1" || raw.expanded === "true") {
    out.expanded = true;
  }
  if (raw.anchor === "billing" || raw.anchor === "contract") out.anchor = raw.anchor;
  if (raw.view === "past" || raw.view === "upcoming") out.view = raw.view;
  if (raw.showAll === true || raw.showAll === "1" || raw.showAll === "true") {
    out.showAll = true;
  }
  if (typeof raw.q === "string" && raw.q.length > 0) out.q = raw.q;
  if (typeof raw.sort === "string" && raw.sort.length > 0) out.sort = raw.sort;
  if (raw.dir === "asc" || raw.dir === "desc") out.dir = raw.dir;
  const status = parseList(raw.status);
  if (status?.length) out.status = status;
  const forecast = parseList(raw.forecast);
  if (forecast?.length) out.forecast = forecast;
  const csm = parseList(raw.csm);
  if (csm?.length) out.csm = csm;
  const month = parseList(raw.month);
  if (month?.length) out.month = month;
  const industry = parseList(raw.industry);
  if (industry?.length) out.industry = industry;
  const tier = parseList(raw.tier);
  if (tier?.length) out.tier = tier;
  const ctype = parseList(raw.ctype);
  if (ctype?.length) out.ctype = ctype;
  if (raw.setup === true || raw.setup === "1" || raw.setup === "true") {
    out.setup = true;
  }
  return out;
}

export const renewalsModule: FeatureModule = {
  key: "renewals",
  nav: {
    label: "Renewals",
    icon: TrendingUp,
    to: "/renewals/dashboard",
    requires: "renewals.read",
    order: 25,
    group: "Revenue",
  },
  routes: (rootRoute) => [
    // Bare /renewals → /renewals/dashboard so the sidebar link always works.
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/renewals",
      beforeLoad: () => {
        throw redirect({ to: "/renewals/dashboard" });
      },
      component: () => null,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/renewals/dashboard",
      component: guarded("renewals.read", RenewalsDashboardPage),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/renewals/pipeline",
      component: guarded("renewals.read", PipelinePage),
      validateSearch: validatePipelineSearch,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/renewals/csms",
      component: guarded("renewals.read", CsmsPage),
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/renewals/data",
      component: guarded("renewals.read", DataPage),
      validateSearch: validateRenewalsDataSearch,
    }),
    // Legacy permalink — the drawer now owns the expanded view, addressed by
    // search params on /renewals/pipeline. Old bookmarks still resolve.
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/renewals/accounts/$qbId",
      beforeLoad: ({ params }) => {
        const qb = Number(params.qbId);
        throw redirect({
          to: "/renewals/pipeline",
          search: Number.isFinite(qb)
            ? { account: qb, expanded: true }
            : {},
        });
      },
      component: () => null,
    }),
  ] as unknown as AnyRoute[],
};
